/**
 * ActivityLog ECharts theme definitions.
 *
 * Pack colors (Reports-Packs-Spec.md § 3.6):
 *   AI Usage   = Teal    #0e7c86
 *   O365       = Indigo  #3d4eb8
 *   DevOps     = Slate   #4a5568
 *   SIEM       = Crimson #a23a3a
 *   KB / Umbraco = Olive #7a8b34
 */

export const activityLogLightTheme = {
    color: [
        '#0e7c86',  // AI Usage Teal
        '#3d4eb8',  // O365 Indigo
        '#4a5568',  // DevOps Slate
        '#a23a3a',  // SIEM Crimson
        '#7a8b34',  // KB Olive
        '#c77e07',  // Warn (orange)
        '#a23131',  // Danger (red)
        '#888888',  // Muted (gray)
    ],
    backgroundColor: 'transparent',
    textStyle: {
        fontFamily: '"Segoe UI", system-ui, sans-serif',
        color: '#181B22',
    },
    title: {
        textStyle: {
            color: '#181B22',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            fontSize: 13,
            fontWeight: 700,
        },
        subtextStyle: {
            color: 'rgba(24,27,34,0.55)',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
        },
    },
    legend: {
        textStyle: {
            color: 'rgba(24,27,34,0.75)',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            fontSize: 12,
        },
    },
    tooltip: {
        backgroundColor: '#fff',
        borderColor: '#ECE8DA',
        borderWidth: 1,
        textStyle: {
            color: '#181B22',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            fontSize: 12,
        },
        extraCssText: 'box-shadow: 0 4px 12px rgba(24,27,34,0.12); border-radius: 6px;',
    },
    categoryAxis: {
        axisLine: {
            lineStyle: { color: '#ECE8DA' },
        },
        axisTick: {
            lineStyle: { color: '#ECE8DA' },
        },
        axisLabel: {
            color: 'rgba(24,27,34,0.55)',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            fontSize: 11,
        },
        splitLine: {
            lineStyle: { color: '#F5F3EC' },
        },
    },
    valueAxis: {
        axisLine: {
            lineStyle: { color: '#ECE8DA' },
        },
        axisTick: {
            lineStyle: { color: '#ECE8DA' },
        },
        axisLabel: {
            color: 'rgba(24,27,34,0.55)',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            fontSize: 11,
        },
        splitLine: {
            lineStyle: { color: '#F5F3EC', type: 'dashed' as const },
        },
    },
    line: {
        smooth: false,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2 },
    },
    bar: {
        barMaxWidth: 48,
        itemStyle: { borderRadius: [3, 3, 0, 0] as [number, number, number, number] },
    },
    sankey: {
        emphasis: {
            focus: 'adjacency' as const,
        },
        lineStyle: {
            color: 'gradient',
            curveness: 0.5,
        },
    },
    treemap: {
        breadcrumb: {
            itemStyle: {
                color: '#F5F3EC',
                textStyle: { color: '#181B22' },
            },
        },
    },
    funnel: {
        label: {
            fontSize: 12,
            fontFamily: '"Segoe UI", system-ui, sans-serif',
        },
    },
};

/**
 * Dark theme — mirrors light for now; future dark-mode work fills in proper
 * token values. Registered so `theme="activitylog-dark"` is valid today.
 *
 * R3-followup: populate dark theme tokens once dark-mode CSS variables land.
 */
export const activityLogDarkTheme: typeof activityLogLightTheme = {
    ...activityLogLightTheme,
    backgroundColor: 'transparent',
    textStyle: {
        ...activityLogLightTheme.textStyle,
        color: '#E8E5DC',
    },
    title: {
        ...activityLogLightTheme.title,
        textStyle: {
            ...activityLogLightTheme.title.textStyle,
            color: '#E8E5DC',
        },
        subtextStyle: {
            ...activityLogLightTheme.title.subtextStyle,
            color: 'rgba(232,229,220,0.55)',
        },
    },
    legend: {
        textStyle: {
            ...activityLogLightTheme.legend.textStyle,
            color: 'rgba(232,229,220,0.75)',
        },
    },
    tooltip: {
        ...activityLogLightTheme.tooltip,
        backgroundColor: '#1F2230',
        borderColor: '#2E3347',
        textStyle: {
            ...activityLogLightTheme.tooltip.textStyle,
            color: '#E8E5DC',
        },
    },
};
